/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { Drawer, Button } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { FC, useEffect, useRef, useState } from "react"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { actionCreator } from "store/store"
import {
  LanguageFullName,
  MenuTranslationRequestData,
  MenuTranslationSetting,
  TranslationDetail,
  TranslationFailedResponse,
  TranslationSetting,
} from "types/models/menu"
import { TextType, TranslationType } from "constants/menu"
import { useDispatch } from "react-redux"
import {
  descriptionTranslationHelper,
  generateAutoTranslationQueueKeys,
  isAutoTranslationQueueExists,
  nameTranslationHelper,
} from "utils/translation_helper"
import { camelCase } from "lodash"
import { hasFormErrors } from "utils/form_helper"
import { ChevronLeftUniversal } from "@duxton-web/icons"
import { TroyWebTracker } from "utils/tracking"
import { EventActionEng, EventCategoryEng } from "constants/tracking"
import { fetchUpdateExistingTranslation } from "utils/rtk_migration_helper"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import {
  useGetSingleTranslationMutation,
  useGetVerifyTranslationMutation,
  useUpdateExistingTranslationMutation,
} from "queries/menuApi"
import { actions } from "store/duck/menu"
import { getActionSuccess, getActionFail } from "store/duck/utils/actionUtils"
import styles from "./TranslationDrawer.modules.css"
import { TranslationDrawerForm } from "./TranslationDrawerForm"

interface TranslationDrawerProps extends FormComponentProps {
  visible: boolean
  translationType: TranslationType
  merchantID: string
  merchantGroupID: string
  menuGroupID: string
  menuTranslationSetting?: MenuTranslationSetting
  nameTranslation?: TranslationSetting
  resetNameTranslation?: TranslationSetting
  descriptionTranslation?: TranslationSetting
  resetDescriptionTranslation?: TranslationSetting
  defaultValue: string
  defaultValueDescription?: string
  onClose(): void
  onSave(formValues?: TranslationDetail): void
  destinationKey?: string
  disabled?: boolean
  isAutoTranslationEnabled: boolean
  isEdit?: boolean
  autoTranslationQueue?: Set<string>
}

export const TranslationDrawerComponent: FC<TranslationDrawerProps> = ({
  visible,
  onClose,
  onSave,
  translationType,
  defaultValue,
  defaultValueDescription,
  nameTranslation,
  resetNameTranslation,
  descriptionTranslation,
  resetDescriptionTranslation,
  menuTranslationSetting,
  destinationKey,
  merchantID,
  merchantGroupID,
  menuGroupID,
  form,
  disabled,
  isAutoTranslationEnabled,
  isEdit,
  autoTranslationQueue,
}: TranslationDrawerProps) => {
  const dispatch = useDispatch()
  const [firstLoad, setFirstLoad] = useState(true)
  const [firstLoadDesc, setFirstLoadDesc] = useState(true)
  const { setFieldsValue, setFields, validateFields } = form
  const {
    flags: {
      getSingleTranslation: isRtkGetSingleTranslationEnabled,
      getVerifyTranslation: isRtkGetVerifyTranslationEnabled,
      updateExistingTranslation: isRtkUpdateExistingTranslationEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()
  const [getSingleTranslationRtk] = useGetSingleTranslationMutation()
  const [getVerifyTranslationRtk] = useGetVerifyTranslationMutation()
  const [
    updateExistingTranslationMutation,
  ] = useUpdateExistingTranslationMutation()

  const defaultValueRef = useRef(defaultValue)
  const defaultValueDescriptionRef = useRef(defaultValueDescription)

  const textMap = useTranslationsMapTyped({
    confirm: "common.confirm",
    explanationHeader: "menuTranslation.explanationHeader",
    itemLabel: "menuTranslation.itemLabel",
    categoryLabel: "menuTranslation.categoryLabel",
    optionLabel: "menuTranslation.optionLabel",
    optionGroupLabel: "menuTranslation.optionGroupLabel",
    editTranslation: "menuTranslation.editTranslation",
    errorExceedLimit: "menuTranslation.errorExceedLimit",
    explanationDetail: "menuTranslation.explanationDetail",
    errorRequired: "menuTranslation.errorRequired",
    descriptionLabel: "menu.drawers.item.labels.description",
  })

  const onResetTranslation = async (
    position: MenuTranslationRequestData["textType"],
    targetLang: string,
    initialValue?: string,
  ) => {
    const data: MenuTranslationRequestData = {
      textType: position,
      entity: camelCase(
        translationType,
      ) as MenuTranslationRequestData["entity"],
      text: initialValue || "",
      textSourceLang: menuTranslationSetting?.menuDefaultLanguage || "",
      textTargetLang: [targetLang],
      destinationKey,
    }

    // Keys include a timestamp; reuse one array for pending + success/fail so
    // autoTranslationQueue $remove matches the $add from the initial dispatch.
    const autoTranslationQueueKeys = generateAutoTranslationQueueKeys(data)

    if (!isRtkFlagLoading && isRtkGetSingleTranslationEnabled) {
      dispatch({
        type: actions.GET_SINGLE_TRANSLATION,
        params: data,
        keys: autoTranslationQueueKeys,
      })

      const result = await getSingleTranslationRtk({
        merchantID,
        merchantGroupID,
        menuGroupID,
        textType: data.textType,
        entity: data.entity,
        text: data.text,
        textSourceLang: data.textSourceLang,
        textTargetLang: data.textTargetLang,
        destinationKey: data.destinationKey,
      })

      if (result.error) {
        const mutationError = result.error
        const errorData =
          "data" in mutationError
            ? (mutationError.data as { error?: unknown })?.error
            : undefined
        dispatch({
          type: getActionFail(actions.GET_SINGLE_TRANSLATION),
          payload: { data: {} },
          meta: {
            previousAction: {
              type: actions.GET_SINGLE_TRANSLATION,
              params: data,
              keys: autoTranslationQueueKeys,
              payload: {
                request: {
                  data: {
                    ...data,
                    ...(menuGroupID ? { menuGroupID } : {}),
                  },
                },
              },
            },
          },
          error: {
            response: {
              data: errorData,
            },
          },
        })
      } else if (result.data) {
        dispatch({
          type: getActionSuccess(actions.GET_SINGLE_TRANSLATION),
          payload: { data: result.data },
          meta: {
            previousAction: {
              type: actions.GET_SINGLE_TRANSLATION,
              params: data,
              keys: autoTranslationQueueKeys,
              payload: {
                request: {
                  data: {
                    ...data,
                    ...(menuGroupID ? { menuGroupID } : {}),
                  },
                },
              },
            },
          },
        })
      }

      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.getSingleTranslation,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: isRtkGetSingleTranslationEnabled,
          FROM_RTK_QUERY: true,
          FROM_OLD_REDUX: false,
          ACTION_NAME: "getSingleTranslation",
        },
      })
    } else if (!isRtkFlagLoading) {
      dispatch(
        actionCreator.menu.getSingleTranslation({
          data,
          merchantID,
          merchantGroupID,
          menuGroupID,
        }),
      )

      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.getSingleTranslation,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: isRtkGetSingleTranslationEnabled,
          FROM_RTK_QUERY: false,
          FROM_OLD_REDUX: true,
          ACTION_NAME: "getSingleTranslation",
        },
      })
    }
  }

  // Shared by the RTK and legacy-redux verify-failure paths in handleSave.
  const setTranslationFieldErrors = (
    messageError: TranslationFailedResponse[] | undefined,
    formValues: any,
  ) => {
    messageError?.forEach((value: TranslationFailedResponse) => {
      let existingValue: string
      let targetForm: string
      if (value.target?.includes("Name")) {
        existingValue = nameTranslationHelper.getTranslation(
          formValues.nameTranslation,
          value.language || "",
        )
        targetForm = nameTranslationHelper.getFormKey(value.language)
      } else {
        existingValue = descriptionTranslationHelper.getTranslation(
          formValues.descriptionTranslation,
          value.language || "",
        )
        targetForm = descriptionTranslationHelper.getFormKey(value.language)
      }
      let errorValue: string
      if (value.target?.includes("Exceed")) {
        errorValue = textMap.errorExceedLimit.replace(
          "{x}",
          value.message || "",
        )
      } else if (value.target?.includes("Empty")) {
        errorValue = textMap.errorRequired
      } else {
        errorValue = value.message || value.target
      }

      setFields({
        [targetForm]: {
          value: existingValue,
          errors: [new Error(errorValue)],
        },
      })
    })
  }

  const handleSave = () => {
    validateFields(async (formError: any, formValues: any) => {
      if (!formError) {
        const finalData = {
          ...formValues,
          entityType: camelCase(translationType),
          // entityID: need clarify on what to pass
        }

        if (!isRtkFlagLoading && isRtkGetVerifyTranslationEnabled) {
          const result = await getVerifyTranslationRtk({
            merchantID,
            merchantGroupID,
            menuGroupID,
            ...finalData,
          })

          TroyWebTracker.sendEvent({
            category: EventCategoryEng.rtkMigration,
            action: EventActionEng.getVerifyTranslation,
            labels: {
              MERCHANT_ID: merchantID,
              IS_RTK_ENABLED: isRtkGetVerifyTranslationEnabled,
              FROM_RTK_QUERY: true,
              FROM_OLD_REDUX: false,
              ACTION_NAME: "getVerifyTranslation",
            },
          })

          if (result.error) {
            const errorData = (result.error as any)?.data
            const messageError =
              typeof errorData?.message === "string"
                ? JSON.parse(errorData.message)
                : errorData?.message

            TroyWebTracker.sendEvent({
              category: EventCategoryEng.verifyTranslation,
              action: EventActionEng.verifyTranslationFail,
            })
            setTranslationFieldErrors(messageError, formValues)
          } else {
            TroyWebTracker.sendEvent({
              category: EventCategoryEng.verifyTranslation,
              action: EventActionEng.verifyTranslationSuccess,
            })
            onSave(formValues)
          }
        } else if (!isRtkFlagLoading) {
          const resp: any = await dispatch(
            actionCreator.menu.getVerifyTranslation({
              data: finalData,
              merchantID,
              merchantGroupID,
              menuGroupID,
            }),
          )

          TroyWebTracker.sendEvent({
            category: EventCategoryEng.rtkMigration,
            action: EventActionEng.getVerifyTranslation,
            labels: {
              MERCHANT_ID: merchantID,
              IS_RTK_ENABLED: isRtkGetVerifyTranslationEnabled,
              FROM_RTK_QUERY: false,
              FROM_OLD_REDUX: true,
              ACTION_NAME: "getVerifyTranslation",
            },
          })

          if (resp && resp.type.endsWith("_SUCCESS")) {
            TroyWebTracker.sendEvent({
              category: EventCategoryEng.verifyTranslation,
              action: EventActionEng.verifyTranslationSuccess,
            })
            onSave(formValues)
          } else if (resp && resp.type.endsWith("_FAIL")) {
            const data = resp.error?.response?.data
            const messageError =
              data?.message && typeof data.message !== "string"
                ? JSON.parse(data.message)
                : undefined

            TroyWebTracker.sendEvent({
              category: EventCategoryEng.verifyTranslation,
              action: EventActionEng.verifyTranslationFail,
            })
            setTranslationFieldErrors(messageError, formValues)
          }
        }
      }
    })
  }

  useEffect(() => {
    defaultValueRef.current = defaultValue
  }, [defaultValue])

  useEffect(() => {
    defaultValueDescriptionRef.current = defaultValueDescription
  }, [defaultValueDescription])

  useEffect(() => {
    if (
      resetNameTranslation?.translation &&
      resetNameTranslation?.originalText === defaultValueRef.current
    ) {
      Object.entries(resetNameTranslation.translation).map(([key, value]) =>
        setFieldsValue({
          [nameTranslationHelper.getFormKey(key)]: value,
        }),
      )
    }
    if (
      resetDescriptionTranslation?.translation &&
      resetDescriptionTranslation?.originalText ===
        defaultValueDescriptionRef.current &&
      translationType === TranslationType.item
    ) {
      Object.entries(resetDescriptionTranslation.translation).map(
        ([key, value]) =>
          setFieldsValue({
            [descriptionTranslationHelper.getFormKey(key)]: value,
          }),
      )
    }
  }, [
    resetDescriptionTranslation,
    resetNameTranslation,
    setFieldsValue,
    translationType,
  ])

  // HINT: Auto Translation on first load in edit category and item, modifier and modifierGroup logic located in handleOnClickEditModifier on ModifierGroups
  useEffect(() => {
    if (
      isAutoTranslationEnabled &&
      isEdit &&
      defaultValue?.trim().length &&
      firstLoad &&
      !isRtkFlagLoading &&
      (translationType === TranslationType.item ||
        translationType === TranslationType.category)
    ) {
      const callingAutoTranslation = menuTranslationSetting?.translationModelSupportedLanguages?.filter(
        value => {
          return !nameTranslation?.translation[value] ? value : null
        },
      )
      if (callingAutoTranslation?.length) {
        const data: MenuTranslationRequestData = {
          textType: "name",
          entity: camelCase(
            translationType,
          ) as MenuTranslationRequestData["entity"],
          text: defaultValue,
          textSourceLang: menuTranslationSetting?.menuDefaultLanguage || "",
          textTargetLang: callingAutoTranslation,
        }
        fetchUpdateExistingTranslation({
          dispatch,
          data,
          merchantID,
          merchantGroupID,
          menuGroupID,
          isRtkUpdateExistingTranslationEnabled,
          updateExistingTranslationRtk: updateExistingTranslationMutation,
        })
      }
      setFirstLoad(false)
    }
  }, [
    defaultValue,
    dispatch,
    firstLoad,
    isAutoTranslationEnabled,
    isEdit,
    isRtkFlagLoading,
    isRtkUpdateExistingTranslationEnabled,
    menuGroupID,
    menuTranslationSetting,
    merchantID,
    merchantGroupID,
    nameTranslation,
    translationType,
    updateExistingTranslationMutation,
  ])

  // Auto Translation on first load in edit item description
  useEffect(() => {
    if (
      isAutoTranslationEnabled &&
      isEdit &&
      firstLoadDesc &&
      !isRtkFlagLoading &&
      translationType === TranslationType.item &&
      defaultValueDescription?.trim().length
    ) {
      const callingDescAutoTranslation = menuTranslationSetting?.translationModelSupportedLanguages?.filter(
        value => {
          return !descriptionTranslation?.translation[value] ? value : null
        },
      )
      if (callingDescAutoTranslation?.length) {
        const data: MenuTranslationRequestData = {
          textType: "description",
          entity: camelCase(
            translationType,
          ) as MenuTranslationRequestData["entity"],
          text: defaultValueDescription,
          textSourceLang: menuTranslationSetting?.menuDefaultLanguage || "",
          textTargetLang: callingDescAutoTranslation,
        }
        fetchUpdateExistingTranslation({
          dispatch,
          data,
          merchantID,
          merchantGroupID,
          menuGroupID,
          isRtkUpdateExistingTranslationEnabled,
          updateExistingTranslationRtk: updateExistingTranslationMutation,
        })
      }
      setFirstLoadDesc(false)
    }
  }, [
    defaultValueDescription,
    descriptionTranslation,
    dispatch,
    firstLoadDesc,
    isAutoTranslationEnabled,
    isEdit,
    isRtkFlagLoading,
    isRtkUpdateExistingTranslationEnabled,
    menuGroupID,
    menuTranslationSetting,
    merchantID,
    merchantGroupID,
    translationType,
    updateExistingTranslationMutation,
  ])

  const getFormName = (location: TranslationType): string => {
    switch (location) {
      case TranslationType.item:
        return textMap.itemLabel
      case TranslationType.category:
        return textMap.categoryLabel
      case TranslationType.modifier:
        return textMap.optionLabel
      case TranslationType.modifierGroup:
        return textMap.optionGroupLabel
      default:
        return textMap.itemLabel
    }
  }
  const supportedLanguage =
    menuTranslationSetting?.menuSupportedLanguages?.map(languageKey => {
      return menuTranslationSetting?.languageFullNames?.[
        languageKey as keyof LanguageFullName
      ]
    }) || []

  const isLoadingNameTranslation = (targetLang: keyof LanguageFullName) =>
    isAutoTranslationQueueExists({
      queue: autoTranslationQueue,
      entityType: translationType,
      textType: TextType.name,
      targetLang,
    })
  const isLoadingDescriptionTranslation = (
    targetLang: keyof LanguageFullName,
  ) =>
    isAutoTranslationQueueExists({
      queue: autoTranslationQueue,
      entityType: translationType,
      textType: TextType.description,
      targetLang,
    })

  return (
    <div data-testid="translation-drawer">
      <Drawer
        title={textMap.editTranslation}
        open={visible}
        onClose={onClose}
        width={450}
        destroyOnClose
        closeIcon={<ChevronLeftUniversal />}
      >
        <div className={styles.content}>
          <div className={styles.contentHeader}>
            {textMap.explanationHeader}
            <br />
            <br />
            {textMap.explanationDetail.replace(
              "{language 1}",
              supportedLanguage.join(", "),
            )}
          </div>
          {menuTranslationSetting?.menuSupportedLanguages?.map(
            (key: keyof LanguageFullName) => (
              <div>
                <div className={styles.languageBarrier}>
                  {menuTranslationSetting.languageFullNames?.[
                    key
                  ]?.toUpperCase()}
                </div>
                <div className={styles.translationForm}>
                  <TranslationDrawerForm
                    initialValue={
                      nameTranslation &&
                      nameTranslationHelper.getTranslation(nameTranslation, key)
                    }
                    form={form}
                    formName={getFormName(translationType)}
                    formKey={nameTranslationHelper.getFormKey(key)}
                    onResetTranslation={() =>
                      onResetTranslation("name", key, defaultValue)
                    }
                    isAutoTranslate={
                      isAutoTranslationEnabled &&
                      menuTranslationSetting.translationModelSupportedLanguages?.includes(
                        key,
                      )
                    }
                    isMandatory={menuTranslationSetting.translationFieldMandatoryLanguages?.includes(
                      key,
                    )}
                    disabled={disabled || isLoadingNameTranslation(key)}
                    loading={isLoadingNameTranslation(key)}
                  />
                </div>
                {translationType === TranslationType.item && (
                  <div className={styles.translationForm}>
                    <TranslationDrawerForm
                      initialValue={
                        descriptionTranslation &&
                        descriptionTranslationHelper.getTranslation(
                          descriptionTranslation,
                          key,
                        )
                      }
                      form={form}
                      formName={textMap.descriptionLabel}
                      formKey={descriptionTranslationHelper.getFormKey(key)}
                      onResetTranslation={() =>
                        onResetTranslation(
                          "description",
                          key,
                          defaultValueDescription,
                        )
                      }
                      isAutoTranslate={
                        isAutoTranslationEnabled &&
                        menuTranslationSetting.translationModelSupportedLanguages?.includes(
                          key,
                        )
                      }
                      disabled={
                        disabled || isLoadingDescriptionTranslation(key)
                      }
                      loading={isLoadingDescriptionTranslation(key)}
                    />
                  </div>
                )}
              </div>
            ),
          )}
        </div>
        <div className={styles.drawerFooter}>
          <Button
            type="primary"
            block
            data-testid="btn-submit"
            onClick={handleSave}
            disabled={hasFormErrors(form) || disabled}
          >
            {textMap.confirm}
          </Button>
        </div>
      </Drawer>
    </div>
  )
}

export const TranslationDrawer = Form.create<TranslationDrawerProps>({})(
  TranslationDrawerComponent,
)
